import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { updateProductQuantityTotal } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #abortController = new AbortController();
  #selectionGeneration = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    const { signal } = this.#abortController;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.#updateQuantityTotal();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateQuantityTotal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #getProductForm() {
    if (this.closest('product-card')) return null;
    const container = this.closest('dialog, .shopify-section');
    return container?.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`) ?? null;
  }

  #handleQuantityUpdate = (event) => {
    if (event.detail?.cartLine || !(event.target instanceof Node)) return;
    if (!this.#getProductForm()?.contains(event.target)) return;
    this.#updateQuantityTotal();
  };

  #updateQuantityTotal() {
    const productForm = this.#getProductForm();
    if (!productForm) return;
    const input = /** @type {HTMLInputElement | null} */ (productForm.querySelector('input[name="quantity"]'));
    const quantity = Number(input?.value || productForm.dataset.quantityDefault || '1');
    updateProductQuantityTotal(this.refs.priceContainer, quantity);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const generation = ++this.#selectionGeneration;

    event.promise
      .then(({ detail }) => {
        if (generation !== this.#selectionGeneration || !this.isConnected) return;
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
          super.updatedCallback();
          queueMicrotask(() => this.#updateQuantityTotal());
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

